import { useState, useCallback } from 'react';
import { api } from '../api/client';

/**
 * SF-171: extracted from `IntelButtons.tsx` — the actual API calls (cast Crystal Ball / thievery
 * Spy on Military & Survey), decoupled from that component's 28x24px button presentation so the
 * mobile intel cards' full-width gather buttons and header refresh icon can drive the exact same
 * underlying action. Display-agnostic by design: does not call `addToast()` itself, since desktop
 * and mobile need different result-message destinations (desktop: `useToast()`'s global toast;
 * mobile: `MobileTargetDetailView`'s own local sticky banner) — see `onResult`.
 */

export type GatherIntelType = 'CB' | 'SOM' | 'SURVEY';

/** Matches `IntelButtons.tsx`'s existing `ButtonState` union verbatim (not `'idle'`). */
export type ButtonState = 'default' | 'loading' | 'done';

export interface UseGatherIntelOptions {
  // SF-181: 'warning' is Crystal Ball-only (a non-blocking fizzle, distinct from a genuine
  // response.error failure) — SOM/SURVEY never emit it.
  onResult?: (message: string, type: 'success' | 'failure' | 'warning') => void;
  /**
   * Fires whenever the API call resolves with a response body present, on either success or
   * failure — matching `IntelButtons.tsx`'s verified actual behavior (`onIntelGathered?.()` is
   * called inside the `else if (response.data)` branch, unconditional on `r.success`).
   */
  onGathered?: () => void;
}

interface CastResult {
  success: boolean;
  runesConsumed?: number;
  manaConsumed?: number;
  wizardsLost?: number;
}

interface ThieveryResult {
  success: boolean;
  stealth?: number;
  thievesSent?: number;
  thievesLost?: number;
}

export function useGatherIntel(
  worldId: string,
  playerProvinceId: string,
  targetProvinceId: string,
  options: UseGatherIntelOptions = {}
) {
  const { onResult, onGathered } = options;
  const [state, setState] = useState<Record<GatherIntelType, ButtonState>>({
    CB: 'default',
    SOM: 'default',
    SURVEY: 'default',
  });

  const castCrystalBall = useCallback(async () => {
    setState((prev) => ({ ...prev, CB: 'loading' }));
    try {
      const response = await api.post<{ result: CastResult }>(
        `/api/worlds/${worldId}/provinces/${playerProvinceId}/cast`,
        { spellType: 'CRYSTAL_BALL', targetProvinceId }
      );

      if (response.error) {
        onResult?.(response.error, 'failure');
      } else if (response.data) {
        const r = response.data.result;
        if (r.success) {
          onResult?.(`Consumed ${Math.round(r.runesConsumed ?? 0)} runes, ${Math.round(r.manaConsumed ?? 0)}% mana`, 'success');
          setState((prev) => ({ ...prev, CB: 'done' }));
        } else {
          let msg = `Spell fizzled. ${Math.round(r.manaConsumed ?? 0)}% mana consumed`;
          if (r.wizardsLost && r.wizardsLost > 0) {
            msg += `, ${r.wizardsLost} wizard(s) killed`;
          }
          onResult?.(msg, 'warning');
        }
        onGathered?.();
      }
    } catch {
      onResult?.('Crystal Ball failed', 'failure');
    } finally {
      setState((prev) => (prev.CB === 'loading' ? { ...prev, CB: 'default' } : prev));
    }
  }, [worldId, playerProvinceId, targetProvinceId, onResult, onGathered]);

  const runThievery = useCallback(
    async (type: 'SOM' | 'SURVEY', operation: 'SPY_ON_MILITARY' | 'SURVEY') => {
      setState((prev) => ({ ...prev, [type]: 'loading' }));
      try {
        const response = await api.post<ThieveryResult>(
          `/api/worlds/${worldId}/provinces/${playerProvinceId}/thievery`,
          { operationName: operation, targetProvinceId, damageTier: null }
        );

        if (response.error) {
          onResult?.(response.error, 'failure');
        } else if (response.data) {
          const r = response.data;
          const sent = r.thievesSent ?? 0;
          if (r.success) {
            onResult?.(`${sent} thieves sent, ${Math.round(r.stealth ?? 0)}% stealth`, 'success');
            setState((prev) => ({ ...prev, [type]: 'done' }));
          } else {
            const lost = r.thievesLost ?? 0;
            onResult?.(`${lost} ${lost === 1 ? 'thief' : 'thieves'} lost, ${Math.round(r.stealth ?? 0)}% stealth`, 'failure');
          }
          onGathered?.();
        }
      } catch {
        onResult?.(`${operation === 'SPY_ON_MILITARY' ? 'Spy on Military' : 'Survey'} failed`, 'failure');
      } finally {
        setState((prev) => (prev[type] === 'loading' ? { ...prev, [type]: 'default' } : prev));
      }
    },
    [worldId, playerProvinceId, targetProvinceId, onResult, onGathered]
  );

  const gather = useCallback(
    async (type: GatherIntelType) => {
      if (type === 'CB') {
        await castCrystalBall();
      } else if (type === 'SOM') {
        await runThievery('SOM', 'SPY_ON_MILITARY');
      } else {
        await runThievery('SURVEY', 'SURVEY');
      }
    },
    [castCrystalBall, runThievery]
  );

  return { gather, state };
}
